import { useState, useRef, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { useIsPasswordExpired } from "~/store/credentials";
import { useSecretKeyDialog } from "~/hooks/useSecretKeyDialog";
import { AUTH_STORE_INSTANCE } from "~/store/auth";

export interface SearchInputConfig {
  searchFilter: string;
  shouldClearInput?: boolean;
  setSearchFilter: (filter: string) => void;
  clearSearchFilter: () => void;
  resetShouldClearInput?: () => void;
  onSearch?: (value: string, shouldInvalidate?: boolean) => Promise<void> | void;
  onClear?: () => Promise<void> | void;
  minSearchLength?: number;
  placeholder?: string;
  requireAuth?: boolean;
}

export function useSearchInput(config: SearchInputConfig) {
  const {
    searchFilter,
    shouldClearInput = false,
    setSearchFilter,
    clearSearchFilter,
    resetShouldClearInput,
    onSearch,
    onClear,
    minSearchLength = 3,
    requireAuth = true,
  } = config;

  // Inicializa com o valor do filtro ativo para manter o input sincronizado
  const [inputValue, setInputValue] = useState<string>(searchFilter);
  const [prevSearchFilter, setPrevSearchFilter] = useState(searchFilter);
  const inputRef = useRef<HTMLInputElement>(null);
  const isPasswordExpired = useIsPasswordExpired();
  const {
    selectors: { useIsAuthorized },
  } = AUTH_STORE_INSTANCE;
  const isUserAuthorized = useIsAuthorized();
  const { openSecretKeyDialog } = useSecretKeyDialog();

  const setFilter = useCallback(async (value: string) => {
    setSearchFilter(value);
  }, [setSearchFilter]);

  const handleSearch = useCallback(async (value: string, shouldInvalidate = true) => {
    if (value?.length >= minSearchLength) {
      await setFilter(value);
    } else {
      clearSearchFilter();
    }

    if (requireAuth && (!isUserAuthorized || isPasswordExpired)) {
      openSecretKeyDialog();
    }

    if (onSearch) {
      await onSearch(value, shouldInvalidate);
    }
  }, [clearSearchFilter, setFilter, onSearch, minSearchLength, requireAuth, isUserAuthorized, isPasswordExpired, openSecretKeyDialog]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    if (value === '') {
      clearSearchFilter();
      if (requireAuth && (!isUserAuthorized || isPasswordExpired)) {
        openSecretKeyDialog();
      }
      if (onClear) {
        requestAnimationFrame(async () => {
          await onClear();
        });
      }
    }
  }, [clearSearchFilter, onClear, requireAuth, isUserAuthorized, isPasswordExpired, openSecretKeyDialog]);

  useEffect(() => {
    if (shouldClearInput && resetShouldClearInput) {
      setInputValue("");
      resetShouldClearInput();
      return;
    }

    // só mantem o valor do searchFilter no input caso o usuário não tenha digitado nada
    if (searchFilter !== prevSearchFilter) {
      if (searchFilter) {
        setInputValue(searchFilter);
      }
      setPrevSearchFilter(searchFilter);
    }
  }, [searchFilter, prevSearchFilter, shouldClearInput, resetShouldClearInput]);

  const clearInput = useCallback(() => {
    setInputValue("");

    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
    }

    clearSearchFilter();
    if (requireAuth && (!isUserAuthorized || isPasswordExpired)) {
      openSecretKeyDialog();
    }
    if (onClear) {
      requestAnimationFrame(async () => {
        await onClear();
      });
    }
  }, [clearSearchFilter, onClear, requireAuth, isUserAuthorized, isPasswordExpired, openSecretKeyDialog]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const value = (e.target as HTMLInputElement).value;
      const trimmedInputValue = value.trim();

      const shouldInvalidate = trimmedInputValue.length >= minSearchLength;
      if (!shouldInvalidate) {
        toast("Erro", {
          description: `A busca deve conter pelo menos ${minSearchLength} caracteres`,
        });
        return;
      }

      handleSearch(trimmedInputValue, shouldInvalidate);
    }
  }, [handleSearch, minSearchLength]);

  return {
    inputValue,
    inputRef,
    handleChange,
    handleKeyDown,
    clearInput,
    handleSearch,
  };
}
